import { MUSCLE_LABELS } from "../../../constants/labels";
import { RECOVERY_MUSCLES } from "../../../constants/recovery-groups";
import { cn } from "@/lib/utils";
import { type Muscle } from "@complete-athlete/enums";
import {
  BODY_FRAME,
  BODY_PATHS,
  BODY_SEAM,
  BODY_SILHOUETTE,
  MUSCLE_PORTRAIT,
  MUSCLE_SHAPES,
  RECOVERED,
  showsMuscle,
  type BodyView,
  type Box,
  type MuscleShape,
} from "@complete-athlete/training-engine";

/** Green when ready, amber when nearly there, rose when still sore. One scale everywhere. */
export const recoveryTone = (value: number) =>
  value >= RECOVERED ? "text-primary" : value >= 60 ? "text-amber-500" : "text-rose-500";

const viewBox = (box: Box) => box.join(" ");

/**
 * The figure in two quiet tones: the fill stacks, so the muscles read lighter than the body under them. Muscles are
 * lit on top of it. `paths` keeps a cropped picture to what it shows.
 */
function BodyBase({ view, paths }: { view: BodyView; paths?: readonly number[] }) {
  const all = BODY_PATHS[view];
  return (
    <g className="fill-foreground/[0.11] dark:fill-foreground/[0.13]">
      <path d={BODY_SILHOUETTE[view]} />
      <g {...BODY_SEAM}>
        {(paths ?? all.map((_, index) => index)).map((index) => (
          <path key={index} d={all[index]} />
        ))}
      </g>
    </g>
  );
}

function MuscleShapes({ shapes }: { shapes: readonly MuscleShape[] }) {
  return shapes.map((shape) => <path key={shape.path} d={BODY_PATHS[shape.view][shape.path]} />);
}

/** One muscle lit up on the body around it, framed the way Gravl pictures each muscle. */
export function MusclePortrait({ muscle, value, className }: { muscle: Muscle; value: number; className?: string }) {
  const { view, frame, paths } = MUSCLE_PORTRAIT[muscle];
  return (
    <svg viewBox={viewBox(frame)} preserveAspectRatio="xMidYMid slice" aria-hidden className={className}>
      <BodyBase view={view} paths={paths} />
      <g {...BODY_SEAM} className={cn("fill-current transition-colors duration-500", recoveryTone(value))}>
        <MuscleShapes shapes={MUSCLE_SHAPES[muscle].filter((shape) => shape.view === view)} />
      </g>
    </svg>
  );
}

/** An exercise's main muscle on the corner of its photo. Position and size come from `className`. */
export function MuscleBadge({ muscle, className }: { muscle: Muscle; className: string }) {
  return (
    <span className={cn("absolute overflow-hidden rounded-lg bg-card ring-2 ring-card", className)}>
      <MusclePortrait muscle={muscle} value={RECOVERED} className="size-full" />
    </span>
  );
}

/**
 * Front and back of the body, every drawn muscle tinted by `toneOf` (a text colour class: the
 * muscle fills with `currentColor`) and named in its tooltip by `titleOf`. The recovery map and
 * the training heatmap are both this figure.
 */
export function MuscleMap({
  toneOf,
  titleOf,
  onPick,
  className,
}: {
  toneOf: (muscle: Muscle) => string;
  titleOf: (muscle: Muscle) => string;
  onPick?: (muscle: Muscle) => void;
  className?: string;
}) {
  return (
    <div className={cn("grid grid-cols-2 gap-3", className)}>
      {(["front", "back"] as const).map((view) => (
        <svg key={view} viewBox={viewBox(BODY_FRAME[view])} aria-hidden className="h-full w-full">
          <BodyBase view={view} />
          {RECOVERY_MUSCLES.map((muscle) => {
            const shapes = MUSCLE_SHAPES[muscle].filter((shape) => shape.view === view);
            if (!shapes.length) return null;
            return (
              <g
                key={muscle}
                {...BODY_SEAM}
                onClick={onPick ? () => onPick(muscle) : undefined}
                className={cn(
                  "fill-current transition-[color,opacity] duration-500",
                  onPick && "cursor-pointer [@media(hover:hover)]:hover:opacity-80",
                  toneOf(muscle),
                )}
              >
                <title>{titleOf(muscle)}</title>
                <MuscleShapes shapes={shapes} />
              </g>
            );
          })}
        </svg>
      ))}
    </div>
  );
}

/** Every muscle tinted by how rested it is, so the state of the whole body reads at a glance. */
export function RecoveryMap({
  values,
  onPick,
  className,
}: {
  values: Record<Muscle, number>;
  onPick: (muscle: Muscle) => void;
  className?: string;
}) {
  return (
    <MuscleMap
      toneOf={(muscle) => recoveryTone(values[muscle])}
      titleOf={(muscle) => `${MUSCLE_LABELS[muscle]()} · ${values[muscle]}%`}
      onPick={onPick}
      className={className}
    />
  );
}

/**
 * What an exercise works: its main muscles lit, the ones it helps with dimmer, on the side of the
 * body they are on (both sides when they are on both). The picture of an exercise with no photo.
 */
export function ExerciseMuscles({
  primary,
  secondary = [],
  className,
}: {
  primary: readonly Muscle[];
  secondary?: readonly Muscle[];
  className?: string;
}) {
  // The helpers go first, so a main muscle drawn over one stays bright.
  const lit = [...secondary.filter((muscle) => !primary.includes(muscle)), ...primary];
  const views = (["front", "back"] as const).filter((view) => lit.some((muscle) => showsMuscle(muscle, view)));
  return (
    <span className={cn("flex items-center justify-center gap-[6%]", className)}>
      {views.map((view) => (
        <svg key={view} viewBox={viewBox(BODY_FRAME[view])} aria-hidden className="h-full w-auto">
          <BodyBase view={view} />
          {lit.map((muscle) => {
            const shapes = MUSCLE_SHAPES[muscle].filter((shape) => shape.view === view);
            if (!shapes.length) return null;
            return (
              <g
                key={muscle}
                {...BODY_SEAM}
                className={cn("fill-current", primary.includes(muscle) ? "text-primary" : "text-primary/40")}
              >
                <MuscleShapes shapes={shapes} />
              </g>
            );
          })}
        </svg>
      ))}
    </span>
  );
}
